import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';
import { Link, Navigate } from 'react-router-dom';
import { TrendingUp, Users, Mail, DollarSign, Target, FileText, Loader2, AlertTriangle, Flag, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { API } from '../utils/apiBase';


const MetricCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-[#e5e7eb] p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-[#6b7280] text-xs uppercase tracking-wider mb-2">
      <Icon size={14} />
      <span>{label}</span>
    </div>
    <div className="font-satoshi text-3xl font-black text-[#0f1115]">{value}</div>
    {sub && <div className="text-xs text-[#6b7280] mt-1">{sub}</div>}
  </div>
);

const FunnelBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-[#4b5563] font-semibold">{label}</span>
        <span className="text-[#0f1115] font-bold">{value.toLocaleString()}</span>
      </div>
      <div className="h-8 bg-[#f4f5f7] relative overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="h-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
};

/**
 * Verdict banner for the Subject A/B panel. Reads `verdict.status` from the
 * backend telemetry and renders one of:
 *   - collecting              → grey, shows "N more sends needed in arm X"
 *   - no_significant_diff     → amber, shows current lift below threshold
 *   - winner                  → green, declares the winning variant
 *   - inconclusive_control_zero → red, control had no signups yet
 */
const AbVerdictBadge = ({ verdict, ab }) => {
  if (!verdict) return null;
  const { status, min_sends_per_arm, min_impressions_per_arm, min_lift_pct, lift_pct, winner, remaining } = verdict;
  // Unified threshold label: subject test uses `min_sends_per_arm`; CTA test uses
  // `min_impressions_per_arm`. Whichever is present, that's the one to display.
  const minThreshold = min_sends_per_arm ?? min_impressions_per_arm ?? 0;
  const thresholdLabel = min_sends_per_arm ? 'send' : 'impression';

  if (status === 'collecting') {
    const need = remaining
      ? Math.max(...Object.values(remaining).map(n => Number(n) || 0))
      : 0;
    return (
      <div
        className="mb-4 p-3 bg-[#f4f5f7] border border-[#e5e7eb] text-xs"
        data-testid="ab-verdict-collecting"
      >
        <div className="flex items-center justify-between">
          <span className="font-bold text-[#4b5563] uppercase tracking-wider">Collecting data</span>
          <span className="text-[#9ca3af]">≥{minThreshold} / arm, ≥{min_lift_pct}% lift</span>
        </div>
        <p className="text-[#6b7280] mt-1">
          {need > 0 ? <>~{need} more {thresholdLabel}{need === 1 ? '' : 's'} needed before a verdict can be declared.</> : 'Almost there.'}
        </p>
      </div>
    );
  }

  if (status === 'no_significant_diff') {
    return (
      <div
        className="mb-4 p-3 bg-[#fffbeb] border border-[#fde68a] text-xs"
        data-testid="ab-verdict-no-diff"
      >
        <div className="flex items-center justify-between">
          <span className="font-bold text-[#78350f] uppercase tracking-wider">No significant difference</span>
          <span className="text-[#92400e] font-mono">{lift_pct > 0 ? '+' : ''}{lift_pct}% lift</span>
        </div>
        <p className="text-[#92400e] mt-1">
          All arms passed the {minThreshold}-{thresholdLabel} threshold but lift is below {min_lift_pct}%. Keep collecting.
        </p>
      </div>
    );
  }

  if (status === 'winner') {
    const winnerLabel = winner === 'score_jobs' ? 'B · Treatment' : 'A · Control';
    return (
      <div
        className="mb-4 p-3 bg-[#ecfdf5] border border-[#10b981] text-xs"
        data-testid="ab-verdict-winner"
      >
        <div className="flex items-center justify-between">
          <span className="font-bold text-[#065f46] uppercase tracking-wider">🏆 Winner declared</span>
          <span className="text-[#047857] font-mono">{lift_pct > 0 ? '+' : ''}{lift_pct}% lift</span>
        </div>
        <p className="text-[#065f46] mt-1">
          <strong>{winnerLabel}</strong> is winning at {Math.abs(lift_pct)}% relative lift (threshold {min_lift_pct}%).
          {ab?.[winner]?.sent ? <> Based on {ab[winner].sent.toLocaleString()} sends.</> : null}
        </p>
      </div>
    );
  }

  if (status === 'inconclusive_control_zero') {
    return (
      <div
        className="mb-4 p-3 bg-[#fef2f2] border border-[#fecaca] text-xs"
        data-testid="ab-verdict-control-zero"
      >
        <span className="font-bold text-[#991b1b] uppercase tracking-wider">Control has 0 signups</span>
        <p className="text-[#991b1b] mt-1">
          Can't compute lift while the control arm has no conversions. Investigate email deliverability or wait for more data.
        </p>
      </div>
    );
  }

  return null;
};

export default function AdminFunnel() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // ── Recent Leads inspector ────────────────────────────────────────
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsErr, setLeadsErr] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [flaggingEmail, setFlaggingEmail] = useState('');

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    setLeadsErr('');
    try {
      const res = await axios.get(
        `${API}/admin/leads/recent?limit=50&include_test=${includeTest}`,
        { withCredentials: true }
      );
      setLeads(res.data.leads || []);
    } catch (e) {
      setLeadsErr(e.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLeadsLoading(false);
    }
  }, [includeTest]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const flagLead = async (email, isTest) => {
    setFlaggingEmail(email);
    try {
      await axios.post(
        `${API}/admin/leads/${encodeURIComponent(email)}/flag`,
        { is_test: isTest },
        { withCredentials: true }
      );
      // Refresh both the leads table AND the funnel stats (counts will shift).
      await Promise.all([loadLeads(), reloadFunnel()]);
    } catch (e) {
      setLeadsErr(e.response?.data?.detail || 'Failed to update flag');
    } finally {
      setFlaggingEmail('');
    }
  };

  const reloadFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/funnel-stats?days=${days}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load funnel stats');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/funnel-stats?days=${days}`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load funnel stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [days]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const totals = data?.totals || {};
  const window = data?.window || {};
  const conversion = data?.conversion || {};
  const topRoles = data?.top_roles_by_leads || [];
  const funnelMax = Math.max(
    window.free_ats_checks || 0,
    window.leads_captured || 0,
    window.signups || 0,
    window.pro_upgrades || 0,
    1
  );

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-funnel-page">
      <AdminReadinessBanner />
      <AdminSubNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Funnel Analytics</h1>
            <p className="text-sm text-[#6b7280]">Lead → Signup → Pro conversion funnel.</p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${days === d ? 'bg-[#002fa7] text-white' : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]'}`}
                data-testid={`window-${d}d`}
              >
                {d}d
              </button>
            ))}
            <Link to="/admin/conversions" className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-conversions">
              <TrendingUp size={14} /> Conversions
            </Link>
            <Link to="/admin/prod-readiness" className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-prod-readiness">
              Readiness
            </Link>
            <Link to="/admin/broadcast" className="px-3 py-2 text-sm font-semibold bg-[#0f1115] text-white hover:bg-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-broadcast">
              <Mail size={14} /> Broadcast
            </Link>
            <Link to="/admin/users" className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-users">
              Users
            </Link>
          </div>
        </div>

        {err && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2" data-testid="funnel-error">
            <AlertTriangle size={16} /> {err}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#6b7280]"><Loader2 className="animate-spin mr-2" size={20} /> Loading funnel…</div>
        ) : data ? (
          <>
            {/* All-time totals */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="totals-grid">
              <MetricCard icon={Users} label="Total Leads" value={(totals.leads || 0).toLocaleString()} />
              <MetricCard icon={Users} label="Total Users" value={(totals.users || 0).toLocaleString()} />
              <MetricCard icon={Target} label="ATS Checks" value={(totals.free_ats_checks || 0).toLocaleString()} />
              <MetricCard icon={Mail} label="Drips Sent" value={(totals.drip_emails_sent || 0).toLocaleString()} />
              <MetricCard icon={DollarSign} label="Revenue" value={`$${(totals.pro_revenue_usd || 0).toLocaleString()}`} sub="Lifetime, paid txns" />
            </div>

            {/* Funnel bars */}
            <div className="bg-white border border-[#e5e7eb] p-6 mb-8" data-testid="funnel-chart">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-[#002fa7]" /> Last {days} days
              </h2>
              <FunnelBar label="Free ATS Checks" value={window.free_ats_checks || 0} max={funnelMax} color="#002fa7" />
              <FunnelBar label="Leads Captured" value={window.leads_captured || 0} max={funnelMax} color="#0ea5e9" />
              <FunnelBar label="Signups" value={window.signups || 0} max={funnelMax} color="#10b981" />
              <FunnelBar label="Pro Upgrades" value={window.pro_upgrades || 0} max={funnelMax} color="#f59e0b" />
            </div>

            {/* Conversion rates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="conversion-grid">
              <MetricCard icon={TrendingUp} label="ATS Check → Lead" value={`${conversion.ats_check_to_lead_pct || 0}%`} sub="Email capture rate" />
              <MetricCard icon={TrendingUp} label="Lead → Signup" value={`${conversion.lead_to_signup_pct || 0}%`} sub="Lifetime conversion" />
              <MetricCard icon={TrendingUp} label="Signup → Pro" value={`${conversion.signup_to_pro_pct || 0}%`} sub={`In last ${days}d`} />
            </div>

            {/* Free ATS Job-Match Bonus + Subject A/B telemetry */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-[#e5e7eb] p-6" data-testid="job-match-bonus-panel">
                <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-1 flex items-center gap-2">
                  <Target size={18} className="text-[#002fa7]" /> Free ATS Job-Match Bonus
                </h2>
                <p className="text-xs text-[#6b7280] mb-4">
                  One-time job cards embedded in the Free ATS results email. Higher pool size = healthier matches.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#f9fafb] border border-[#e5e7eb]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] mb-1">Bonus emails sent</div>
                    <div className="font-satoshi text-2xl font-black text-[#0f1115]" data-testid="bonus-total-sent">
                      {(data?.free_ats_jobs?.total_sent ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-[#9ca3af] mt-1">
                      {(data?.free_ats_jobs?.window_sent ?? 0).toLocaleString()} in window
                    </div>
                  </div>
                  <div className="p-3 bg-[#f9fafb] border border-[#e5e7eb]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] mb-1">Avg pool size</div>
                    <div className="font-satoshi text-2xl font-black text-[#0f1115]" data-testid="bonus-avg-pool">
                      {data?.free_ats_jobs?.avg_pool_size ?? 0}
                    </div>
                    <div className="text-[11px] text-[#9ca3af] mt-1">jobs/recipient</div>
                  </div>
                  <div className="p-3 bg-[#f9fafb] border border-[#e5e7eb]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] mb-1">Avg shown</div>
                    <div className="font-satoshi text-2xl font-black text-[#0f1115]" data-testid="bonus-avg-shown">
                      {data?.free_ats_jobs?.avg_jobs_shown ?? 0}
                    </div>
                    <div className="text-[11px] text-[#9ca3af] mt-1">cards/email</div>
                  </div>
                  <div className="p-3 bg-[#f9fafb] border border-[#e5e7eb]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] mb-1">Total cards (window)</div>
                    <div className="font-satoshi text-2xl font-black text-[#0f1115]" data-testid="bonus-total-shown">
                      {(data?.free_ats_jobs?.total_jobs_shown_window ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-[#9ca3af] mt-1">signup-CTA impressions</div>
                  </div>
                </div>

                {/* Got-jobs vs No-jobs conversion split — directly answers
                    "does showing job matches actually move signups?" */}
                <div className="mt-4 pt-4 border-t border-[#e5e7eb]" data-testid="jobs-split-panel">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-xs uppercase tracking-wider font-bold text-[#0f1115]">
                      Got Jobs vs No Jobs — Signup Lift
                    </h3>
                    {data?.free_ats_jobs_split?.lift_pct_vs_no_jobs != null && (
                      <span
                        className={`text-xs font-mono font-bold ${data.free_ats_jobs_split.lift_pct_vs_no_jobs >= 0 ? 'text-[#10b981]' : 'text-[#e11d48]'}`}
                        data-testid="jobs-split-lift"
                      >
                        {data.free_ats_jobs_split.lift_pct_vs_no_jobs >= 0 ? '+' : ''}
                        {data.free_ats_jobs_split.lift_pct_vs_no_jobs}% lift
                      </span>
                    )}
                  </div>
                  {['shown', 'shown_jsearch', 'shown_adzuna', 'none_in_pool'].map(cohort => {
                    const row = data?.free_ats_jobs_split?.[cohort] || { sent: 0, signups: 0, signup_rate_pct: 0 };
                    const isAggregateShown = cohort === 'shown';
                    const isJsearch = cohort === 'shown_jsearch';
                    const isAdzuna = cohort === 'shown_adzuna';
                    const isNone = cohort === 'none_in_pool';
                    const dotColor = isAggregateShown
                      ? 'bg-[#10b981]'
                      : isJsearch
                        ? 'bg-[#3b82f6]'
                        : isAdzuna
                          ? 'bg-[#a855f7]'
                          : 'bg-[#9ca3af]';
                    const label = isAggregateShown
                      ? 'Got jobs (any source)'
                      : isJsearch
                        ? '  ↳ from JSearch'
                        : isAdzuna
                          ? '  ↳ from Adzuna (Sentry)'
                          : 'No jobs in pool';
                    return (
                      <div
                        key={cohort}
                        className={`flex items-center justify-between py-2 text-xs ${(isJsearch || isAdzuna) ? 'pl-3 text-[#6b7280]' : ''}`}
                        data-testid={`jobs-split-${cohort}`}
                      >
                        <span className={isNone || isAggregateShown ? 'text-[#4b5563]' : ''}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${dotColor}`} />
                          {label}
                          <span className="text-[#9ca3af] ml-2 font-mono">({row.signups}/{row.sent})</span>
                        </span>
                        <span className={`font-mono font-bold ${isAggregateShown ? 'text-[#0f1115]' : 'text-[#4b5563]'}`}>
                          {row.signup_rate_pct}%
                        </span>
                      </div>
                    );
                  })}
                  {(data?.free_ats_jobs_split?.shown?.sent ?? 0) === 0 && (data?.free_ats_jobs_split?.none_in_pool?.sent ?? 0) === 0 && (
                    <p className="text-[11px] text-[#9ca3af] mt-2" data-testid="jobs-split-empty">
                      No data yet in this window — needs at least one check with `resume_text` per cohort.
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-white border border-[#e5e7eb] p-6" data-testid="subject-ab-panel">
                <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-1 flex items-center gap-2">
                  <Mail size={18} className="text-[#002fa7]" /> Subject Line A/B Test
                </h2>
                <p className="text-xs text-[#6b7280] mb-4">
                  Free ATS results email subject — 50/50 deterministic split by email hash. Signup rate is the conversion metric.
                </p>
                <AbVerdictBadge verdict={data?.subject_ab_test?.verdict} ab={data?.subject_ab_test} />
                {['score', 'score_jobs'].map(variant => {
                  const row = data?.subject_ab_test?.[variant] || { sent: 0, signups: 0, signup_rate_pct: 0 };
                  const isControl = variant === 'score';
                  const label = isControl ? 'Your ATS Score: N/100' : 'Your ATS Score + N matched jobs';
                  const isWinner = data?.subject_ab_test?.verdict?.status === 'winner' && data?.subject_ab_test?.verdict?.winner === variant;
                  return (
                    <div
                      key={variant}
                      className={`mb-3 p-3 border ${isWinner ? 'border-[#10b981] bg-[#ecfdf5]' : 'border-[#e5e7eb]'}`}
                      data-testid={`ab-variant-${variant}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-bold text-[#0f1115] flex items-center gap-2">
                          {isControl ? 'A · Control' : 'B · Treatment'} <span className="font-normal text-[#9ca3af]">— {label}</span>
                          {isWinner && (
                            <span className="px-1.5 py-0.5 bg-[#10b981] text-white text-[10px] font-black uppercase tracking-wider" data-testid={`ab-winner-${variant}`}>
                              Winner
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-mono text-[#4b5563]">
                          {row.signups}/{row.sent}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-satoshi text-2xl font-black text-[#002fa7]">{row.signup_rate_pct}%</span>
                        <span className="text-[11px] text-[#9ca3af]">signup rate</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA Copy A/B Test — Free ATS results bottom button. Hunts
                for the magic words that move clicks from Free ATS → /pricing.
                GA showed 934 free-tool views vs 1 pricing view this year.  */}
            <div className="bg-white border border-[#e5e7eb] p-6 mb-8" data-testid="cta-ab-panel">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-1 flex items-center gap-2">
                <Target size={18} className="text-[#002fa7]" /> CTA Copy A/B Test
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                Free ATS results page · bottom "see plans" button. Sticky 3-way split. Click-through rate is the conversion metric.
              </p>
              <AbVerdictBadge verdict={data?.cta_ab_test?.verdict} ab={data?.cta_ab_test} />
              {['v_unlock_unlimited', 'v_see_pricing', 'v_get_pro_features'].map(variant => {
                const row = data?.cta_ab_test?.[variant] || { impressions: 0, clicks: 0, ctr_pct: 0 };
                const label =
                  variant === 'v_unlock_unlimited' ? 'A · "Unlock unlimited — see plans" (control)'
                  : variant === 'v_see_pricing' ? 'B · "See pricing"'
                  : 'C · "Get Pro features"';
                const isWinner = data?.cta_ab_test?.verdict?.status === 'winner' && data?.cta_ab_test?.verdict?.winner === variant;
                return (
                  <div
                    key={variant}
                    className={`mb-3 p-3 border ${isWinner ? 'border-[#10b981] bg-[#ecfdf5]' : 'border-[#e5e7eb]'}`}
                    data-testid={`cta-ab-variant-${variant}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold text-[#0f1115] flex items-center gap-2">
                        {label}
                        {isWinner && (
                          <span className="px-1.5 py-0.5 bg-[#10b981] text-white text-[10px] font-black uppercase tracking-wider">Winner</span>
                        )}
                      </div>
                      <div className="text-xs font-mono text-[#4b5563]">{row.clicks}/{row.impressions}</div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-satoshi text-2xl font-black text-[#002fa7]">{row.ctr_pct}%</span>
                      <span className="text-[11px] text-[#9ca3af]">click-through rate</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Broadened queries — niche searches that triggered our fallback.
                Gold for SEO landing-page targeting + email-template keywords. */}
            <div className="bg-white border border-[#e5e7eb] p-6 mb-8" data-testid="broadened-queries-panel">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-1 flex items-center gap-2">
                <TrendingUp size={18} className="text-[#002fa7]" /> Niche queries we auto-broadened
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                When a user's exact search returns 0 from JSearch, we fall back to a shorter variant.
                These are high-intent SEO targets — consider building landing pages or email templates for them.
                <span className="ml-1 text-[#9ca3af]">
                  {(data?.broadened_queries?.total_events ?? 0).toLocaleString()} events in window.
                </span>
              </p>
              {(!data?.broadened_queries?.top || data.broadened_queries.top.length === 0) ? (
                <p className="text-sm text-[#6b7280] py-3" data-testid="broadened-empty-state">
                  No broadening events yet in this window. Niche queries will show up here once users hit them.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full text-sm" data-testid="broadened-queries-table">
                    <thead>
                      <tr className="text-xs uppercase tracking-wider text-[#6b7280] border-b border-[#e5e7eb]">
                        <th className="text-left py-2 pr-4 font-semibold">Original query</th>
                        <th className="text-left py-2 pr-4 font-semibold">Broadened to</th>
                        <th className="text-right py-2 pr-4 font-semibold">Searches</th>
                        <th className="text-right py-2 font-semibold">Avg results</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.broadened_queries.top.map((row, i) => (
                        <tr key={`${row.original_query}|${row.broadened_to}|${i}`} className="border-b border-[#f4f5f7] hover:bg-[#f9fafb]">
                          <td className="py-2.5 pr-4 text-[#0f1115] break-all">{row.original_query}</td>
                          <td className="py-2.5 pr-4 text-[#4b5563] break-all">{row.broadened_to}</td>
                          <td className="py-2.5 pr-4 text-right font-mono text-[#0f1115]">{row.search_count}</td>
                          <td className="py-2.5 text-right font-mono text-[#6b7280]">{row.avg_results}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Top-converting roles */}
            <div className="bg-white border border-[#e5e7eb] p-6 mb-8" data-testid="top-roles-panel">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center gap-2">
                <FileText size={18} className="text-[#002fa7]" /> Top roles by lead volume
              </h2>
              {topRoles.length === 0 ? (
                <p className="text-sm text-[#6b7280]">No role-tagged leads yet in this window.</p>
              ) : (
                <ul className="divide-y divide-[#e5e7eb]">
                  {topRoles.map(r => (
                    <li key={r.target_job} className="py-2 flex justify-between text-sm">
                      <span className="text-[#0f1115]">{r.target_job}</span>
                      <span className="text-[#4b5563] font-mono">{r.leads}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent leads inspector — flag test/internal entries so they
                stop polluting the funnel counts above. */}
            <div className="bg-white border border-[#e5e7eb] p-6" data-testid="recent-leads-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2">
                    <Users size={18} className="text-[#002fa7]" /> Recent Leads
                  </h2>
                  <p className="text-xs text-[#6b7280] mt-1">
                    Last 50 captured. Click <strong>Mark as test</strong> on any internal entry — it'll stop counting toward conversion stats above.
                    {data?.excluded_as_test?.leads > 0 && (
                      <> Currently excluding <strong>{data.excluded_as_test.leads}</strong> flagged leads.</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIncludeTest(v => !v)}
                    className="px-3 py-2 text-xs font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5"
                    data-testid="toggle-include-test"
                  >
                    {includeTest ? <><EyeOff size={12} /> Hide test</> : <><Eye size={12} /> Show test</>}
                  </button>
                  <button
                    onClick={loadLeads}
                    disabled={leadsLoading}
                    className="px-3 py-2 text-xs font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="reload-leads"
                  >
                    <RefreshCw size={12} className={leadsLoading ? 'animate-spin' : ''} /> Refresh
                  </button>
                </div>
              </div>

              {leadsErr && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2" data-testid="leads-error">
                  <AlertTriangle size={14} /> {leadsErr}
                </div>
              )}

              {leadsLoading ? (
                <div className="py-12 text-center text-[#6b7280] text-sm flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={16} /> Loading leads…
                </div>
              ) : leads.length === 0 ? (
                <p className="text-sm text-[#6b7280] py-4">No leads captured yet.</p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full text-sm" data-testid="leads-table">
                    <thead>
                      <tr className="text-xs uppercase tracking-wider text-[#6b7280] border-b border-[#e5e7eb]">
                        <th className="text-left py-2 pr-4 font-semibold">Email</th>
                        <th className="text-left py-2 pr-4 font-semibold">Target Job</th>
                        <th className="text-left py-2 pr-4 font-semibold">Score</th>
                        <th className="text-left py-2 pr-4 font-semibold">Source</th>
                        <th className="text-left py-2 pr-4 font-semibold">Captured</th>
                        <th className="text-right py-2 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map(lead => {
                        const captured = lead.created_at ? new Date(lead.created_at).toLocaleString() : '—';
                        return (
                          <tr
                            key={lead.email}
                            className={`border-b border-[#f4f5f7] ${lead.is_test ? 'bg-[#fef3c7]/40 text-[#92400e]' : 'hover:bg-[#f9fafb]'}`}
                            data-testid={`lead-row-${lead.email}`}
                          >
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-[#0f1115] break-all">{lead.email}</span>
                                {lead.is_test && (
                                  <span className="px-1.5 py-0.5 bg-[#fbbf24] text-[#0f1115] text-[10px] font-bold uppercase tracking-wide" data-testid="test-badge">Test</span>
                                )}
                                {lead.converted_to_user && (
                                  <span className="px-1.5 py-0.5 bg-[#10b981] text-white text-[10px] font-bold uppercase tracking-wide">User</span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 pr-4 text-[#4b5563]">{lead.target_job || '—'}</td>
                            <td className="py-2.5 pr-4 text-[#4b5563] font-mono">{lead.last_score ?? '—'}</td>
                            <td className="py-2.5 pr-4 text-[#6b7280] text-xs">{lead.source}</td>
                            <td className="py-2.5 pr-4 text-[#6b7280] text-xs whitespace-nowrap">{captured}</td>
                            <td className="py-2.5 text-right">
                              <button
                                onClick={() => flagLead(lead.email, !lead.is_test)}
                                disabled={flaggingEmail === lead.email}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${lead.is_test ? 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]' : 'bg-[#0f1115] text-white hover:bg-[#002fa7]'}`}
                                data-testid={`flag-btn-${lead.email}`}
                                title={lead.is_test ? 'Mark this as a real lead again' : "Exclude from funnel stats — it's an internal/test entry"}
                              >
                                {flaggingEmail === lead.email ? (
                                  <Loader2 size={11} className="animate-spin" />
                                ) : (
                                  <Flag size={11} />
                                )}
                                {lead.is_test ? 'Restore' : 'Mark as test'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
